import { motion, useScroll, useTransform } from "framer-motion";
import { Activity, Leaf, MapPin, Phone, Waves } from "lucide-react";
import { CLINIC, IMAGES } from "@/data/clinic";
import { LogoMark } from "./Logo";

const SERVICES = [
    { icon: Leaf, name: "Marma Chikitsa", note: "Energy • Balance • Calm" },
    { icon: Activity, name: "Chiropractic Care", note: "Mobility • Alignment • Comfort" },
    { icon: Waves, name: "Nadi Vaidya", note: "Holistic • Natural • Traditional" },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 160]);

    return (
        <section id="home" data-testid="hero-section" className="relative bg-cream">
            <div className="grid min-h-[100svh] lg:grid-cols-2">
                <div className="flex flex-col justify-center px-5 pb-14 pt-32 sm:px-10 lg:px-12 lg:pt-28 xl:px-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5"
                    >
                        <LogoMark className="h-16 w-16 shrink-0 lg:h-20 lg:w-20 xl:h-24 xl:w-24" />
                        <div>
                            <p
                                className="whitespace-nowrap font-serif text-[1.75rem] font-semibold leading-none tracking-[0.03em] text-forest sm:text-4xl lg:text-[2.6rem] xl:text-[3.2rem]"
                                data-testid="hero-brand-name"
                            >
                                AGASTYA CHIRO
                            </p>
                            <p
                                className="mt-2.5 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#8A6D1F] sm:text-xs"
                                data-testid="hero-brand-sub"
                            >
                                <span className="h-px w-6 shrink-0 bg-[#8A6D1F]/50" aria-hidden="true" />
                                and Ancient Marma Therapy
                                <span className="h-px w-6 shrink-0 bg-[#8A6D1F]/50" aria-hidden="true" />
                            </p>
                        </div>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
                        className="mt-8 text-[11px] font-semibold uppercase tracking-[0.32em] text-moss"
                        data-testid="hero-overline"
                    >
                        Traditional Wisdom • Holistic Approach • Personalized Care
                    </motion.p>

                    <h1
                        data-testid="hero-headline"
                        className="mt-5 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
                    >
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: "115%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1, delay: 0.55, ease: EASE }}
                            >
                                Ancient Healing.
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                className="block italic text-forest"
                                initial={{ y: "115%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1, delay: 0.7, ease: EASE }}
                            >
                                Modern Relief.
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                        className="mt-6 max-w-md text-sm leading-relaxed text-moss md:text-base"
                        data-testid="hero-subheadline"
                    >
                        Marma Chikitsa, chiropractic care and Nadi Vaidya approaches, thoughtfully
                        adapted to individual needs.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href={CLINIC.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-book-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-cream transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest-deep"
                        >
                            Book a Consultation
                        </a>
                        <a
                            href={CLINIC.phoneTel}
                            data-testid="hero-call-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-forest px-7 py-3.5 text-sm font-semibold text-forest transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest hover:text-cream"
                        >
                            <Phone className="h-4 w-4" /> Call {CLINIC.phoneDisplay}
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="mt-6 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-moss"
                        data-testid="hero-location"
                    >
                        <MapPin className="h-4 w-4 text-[#8A6D1F]" />
                        {CLINIC.addressShort}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
                        className="mt-8 grid grid-cols-1 gap-6 border-t border-sand pt-7 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-sand"
                        data-testid="hero-services"
                    >
                        {SERVICES.map((s) => (
                            <div key={s.name} className="sm:px-5 sm:first:pl-0">
                                <s.icon className="h-5 w-5 text-terracotta" aria-hidden="true" />
                                <p className="mt-2.5 text-sm font-semibold text-ink">{s.name}</p>
                                <p className="mt-0.5 text-[11px] tracking-wide text-moss">{s.note}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="relative h-[360px] overflow-hidden sm:h-[440px] lg:h-auto">
                    <motion.img
                        style={{ y: bgY }}
                        src={IMAGES.hero}
                        alt="Practitioner providing calm, hands-on Marma Chikitsa care at Agastya Chiro and Ancient Marma Therapy, Kurnool"
                        className="absolute inset-0 h-full w-full scale-110 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/10 to-transparent lg:bg-gradient-to-r lg:from-cream lg:via-cream/10 lg:to-transparent" aria-hidden="true" />
                    <p className="absolute bottom-10 right-8 hidden font-serif text-2xl italic leading-snug text-ink/70 lg:block" aria-hidden="true">
                        Ancient knowledge.
                        <br />
                        Lasting wellness.
                    </p>
                </div>
            </div>
        </section>
    );
}
